import type {
    ILibraryMultiStepReaction,
    ILibraryReaction,
} from "../redux/actions/library";
import type {
    ReactionChemical,
    ReactionRoute,
    ReactionRouteStage,
} from "../data/models/schemas";
import type { IMultiStepReaction } from "../redux/reducers/reactions";

export const buildReactionLookup = (
    reactions: ILibraryReaction[] = []
): Map<string, ILibraryReaction> =>
    new Map(
        reactions
            .filter((reaction) => !!reaction.ui_id)
            .map((reaction) => [reaction.ui_id as string, reaction])
    );

export const buildRouteLookup = (
    routes: ILibraryMultiStepReaction[] = []
): Map<string, ILibraryMultiStepReaction> =>
    new Map(
        routes
            .filter((route) => !!route.ui_id)
            .map((route) => [route.ui_id as string, route])
    );

const getRouteReactions = (
    route: ILibraryMultiStepReaction
): Record<string, ILibraryReaction> => {
    return route.reactions || {};
};

const countDefinedKeys = (value: Record<string, unknown> | undefined): number =>
    Object.values(value || {}).filter(
        (entry) => entry !== undefined && entry !== null
    ).length;

const scoreReactionRichness = (
    reaction: ILibraryReaction | undefined
): number => {
    if (!reaction) {
        return -1;
    }

    const topLevelScore = countDefinedKeys(reaction as Record<string, unknown>);
    const chemicalsScore = (reaction.chemicals || []).reduce(
        (sum, chemical) =>
            sum + countDefinedKeys(chemical as Record<string, unknown>),
        0
    );
    const workupsScore = (reaction.workups || []).reduce(
        (sum, workup) =>
            sum + countDefinedKeys(workup as Record<string, unknown>),
        0
    );

    return topLevelScore + chemicalsScore + workupsScore;
};

const chooseHighestDisplayOrderStage = (
    stages: ReactionRouteStage[]
): ReactionRouteStage | undefined =>
    stages.reduce<ReactionRouteStage | undefined>((best, stage) => {
        if (!best) {
            return stage;
        }
        const bestOrder = best.display_order ?? Number.MIN_SAFE_INTEGER;
        const stageOrder = stage.display_order ?? Number.MIN_SAFE_INTEGER;
        return stageOrder >= bestOrder ? stage : best;
    }, undefined);

export const resolveRouteTargetStage = (
    route?: ILibraryMultiStepReaction
): ReactionRouteStage | undefined => {
    const stages = route?.stages || [];
    if (stages.length === 0) {
        return undefined;
    }

    if (route?.target_stage_ui_id) {
        const byTarget = stages.find(
            (stage) => stage.ui_id === route.target_stage_ui_id
        );
        if (byTarget) {
            return byTarget;
        }
    }

    const predecessorIds = new Set(
        (route?.dependencies || [])
            .map((dependency) => dependency.predecessor_stage_ui_id)
            .filter(Boolean)
    );
    const sinkStages = stages.filter(
        (stage) => !!stage.ui_id && !predecessorIds.has(stage.ui_id)
    );
    if (sinkStages.length === 1) {
        return sinkStages[0];
    }
    if (sinkStages.length > 1) {
        return chooseHighestDisplayOrderStage(sinkStages);
    }

    return chooseHighestDisplayOrderStage(stages);
};

export const normalizeRouteTargetStage = <T extends ILibraryMultiStepReaction>(
    route: T
): T => {
    if (!route.stages?.length) {
        return route;
    }
    const targetStage = resolveRouteTargetStage(route);
    if (!targetStage?.ui_id || route.target_stage_ui_id === targetStage.ui_id) {
        return route;
    }
    return {
        ...route,
        target_stage_ui_id: targetStage.ui_id,
    };
};

type ChemicalIdentityMatchKind = "UI_ID" | "VIRIDISCHEM_ID" | "SMILES" | "NAME";

export type RouteChemicalIdentityRepair = {
    predecessor_stage_ui_id?: string;
    successor_stage_ui_id?: string;
    from_ui_id?: string;
    to_ui_id?: string;
    match_kind?: ChemicalIdentityMatchKind;
    message: string;
};

export type NormalizeReactionRouteChemicalIdentityResult<
    T extends ReactionRoute,
> = {
    route: T;
    repairs: RouteChemicalIdentityRepair[];
    warnings: string[];
};

const ROUTE_PRODUCT_TYPES = new Set(["Product"]);
const ROUTE_INPUT_TYPES = new Set(["Reactant", "Substrate"]);

type ChemicalIdentity = {
    uiId?: string;
    viridischemId?: number;
    smiles?: string;
    name?: string;
};

const normalizeIdentityText = (value?: string): string =>
    (value || "").trim().toLowerCase();

const getChemicalSmiles = (chemical?: Partial<ReactionChemical>): string =>
    chemical?.smiles || chemical?.chemical?.smiles || "";

const getChemicalIdentity = (
    chemical?: Partial<ReactionChemical>,
    fallbackUiId?: string
): ChemicalIdentity => ({
    uiId: chemical?.ui_id || fallbackUiId,
    viridischemId: chemical?.viridischem_id,
    smiles: getChemicalSmiles(chemical),
    name: chemical?.name || chemical?.chemical?.name,
});

const getChemicalIdentityMatchKind = (
    left: ChemicalIdentity,
    right: ChemicalIdentity
): ChemicalIdentityMatchKind | null => {
    if (left.uiId && right.uiId && left.uiId === right.uiId) {
        return "UI_ID";
    }
    if (
        left.viridischemId !== undefined &&
        right.viridischemId !== undefined &&
        left.viridischemId === right.viridischemId
    ) {
        return "VIRIDISCHEM_ID";
    }
    if (left.smiles && right.smiles && left.smiles === right.smiles) {
        return "SMILES";
    }
    const leftName = normalizeIdentityText(left.name);
    const rightName = normalizeIdentityText(right.name);
    if (leftName && rightName && leftName === rightName) {
        return "NAME";
    }
    return null;
};

const cloneReactionForChemicalIdentity = (
    reaction: ILibraryReaction
): ILibraryReaction => ({
    ...reaction,
    chemicals: (reaction.chemicals || []).map((chemical) => ({ ...chemical })),
    workups: reaction.workups ? [...reaction.workups] : reaction.workups,
    products: reaction.products ? [...reaction.products] : reaction.products,
});

const cloneRouteForChemicalIdentity = <T extends ReactionRoute>(route: T): T =>
    ({
        ...route,
        stages: (route.stages || []).map((stage) => ({
            ...stage,
            nested_route: stage.nested_route
                ? cloneRouteForChemicalIdentity(stage.nested_route)
                : stage.nested_route,
        })),
        dependencies: (route.dependencies || []).map((dependency) => ({
            ...dependency,
        })),
        reactions: route.reactions
            ? Object.fromEntries(
                  Object.entries(route.reactions).map(([key, reaction]) => [
                      key,
                      cloneReactionForChemicalIdentity(
                          reaction as ILibraryReaction
                      ),
                  ])
              )
            : route.reactions,
        warnings: route.warnings ? [...route.warnings] : route.warnings,
    }) as T;

const isRouteProductChemical = (chemical: ReactionChemical): boolean =>
    ROUTE_PRODUCT_TYPES.has(String(chemical.chemical_type || ""));

const isRouteInputChemical = (chemical: ReactionChemical): boolean =>
    ROUTE_INPUT_TYPES.has(String(chemical.chemical_type || ""));

const findReactionProductChemical = (
    reaction: ILibraryReaction | undefined,
    preferredOutputUiId?: string
): ReactionChemical | undefined => {
    const chemicals = reaction?.chemicals || [];
    const products = chemicals.filter(isRouteProductChemical);
    return (
        (preferredOutputUiId
            ? products.find(
                  (chemical) => chemical.ui_id === preferredOutputUiId
              )
            : undefined) ||
        (reaction?.produces_chemical_ui_id
            ? products.find(
                  (chemical) =>
                      chemical.ui_id === reaction.produces_chemical_ui_id
              )
            : undefined) ||
        products[0]
    );
};

const resolveReactionFromRouteStage = (
    route: ILibraryMultiStepReaction,
    stage: ReactionRouteStage | undefined,
    reactionLookup: Map<string, ILibraryReaction>
): ILibraryReaction | undefined => {
    if (!stage) {
        return undefined;
    }

    const reactions = getRouteReactions(route);
    const stageId = stage.ui_id;
    if (stageId && reactions[stageId]) {
        return reactions[stageId];
    }

    const reactionUiId = stage.reaction_ui_id;
    if (reactionUiId && reactions[reactionUiId]) {
        return reactions[reactionUiId];
    }

    if (reactionUiId) {
        const byMatchingReactionUiId = Object.values(reactions).find(
            (reaction) => reaction?.ui_id === reactionUiId
        );
        if (byMatchingReactionUiId) {
            return byMatchingReactionUiId;
        }
        const fromLibrary = reactionLookup.get(reactionUiId);
        if (fromLibrary) {
            return fromLibrary;
        }
    }

    const stageKind = stage.stage_kind || "REACTION";
    if (stageKind === "REACTION") {
        const stepByOrder =
            stage.display_order && stage.display_order > 0
                ? reactions[`step${stage.display_order}`]
                : undefined;
        if (stepByOrder) {
            return stepByOrder;
        }
    }

    return undefined;
};

const resolveFinalReactionForRouteStage = (
    stage: ReactionRouteStage | undefined,
    routeLookup: Map<string, ILibraryMultiStepReaction>,
    reactionLookup: Map<string, ILibraryReaction>,
    visited = new Set<string>()
): ILibraryReaction | undefined => {
    if (!stage) {
        return undefined;
    }
    const nestedRoute =
        (stage.nested_route_ui_id
            ? routeLookup.get(stage.nested_route_ui_id)
            : undefined) ||
        (stage.nested_route as ILibraryMultiStepReaction | undefined);
    if (!nestedRoute) {
        return undefined;
    }

    if (nestedRoute.ui_id) {
        if (visited.has(nestedRoute.ui_id)) {
            return undefined;
        }
        visited.add(nestedRoute.ui_id);
    }

    const finalStage = resolveRouteTargetStage(nestedRoute);
    if (!finalStage) {
        return undefined;
    }

    const finalStageKind = finalStage.stage_kind || "REACTION";
    if (finalStageKind === "REACTION") {
        return resolveReactionFromRouteStage(
            nestedRoute,
            finalStage,
            reactionLookup
        );
    }

    return resolveFinalReactionForRouteStage(
        finalStage,
        routeLookup,
        reactionLookup,
        visited
    );
};

type RouteChemicalIdentityContext = {
    repairs: RouteChemicalIdentityRepair[];
    warnings: string[];
};

type ResolvedStageOutput = {
    outputUiId?: string;
    outputChemical?: ReactionChemical;
};

const resolveInlineNestedRoute = (
    stage: ReactionRouteStage | undefined
): ReactionRoute | undefined =>
    (stage?.nested_route as ReactionRoute | undefined) || undefined;

const resolveStageOutputForChemicalIdentity = (
    route: ReactionRoute,
    stage: ReactionRouteStage | undefined
): ResolvedStageOutput => {
    if (!stage) {
        return {};
    }

    const stageKind = stage.stage_kind || "REACTION";
    if (stageKind === "ROUTE") {
        const nestedRoute = resolveInlineNestedRoute(stage);
        const nestedTargetStage = resolveRouteTargetStage(
            nestedRoute as ILibraryMultiStepReaction | undefined
        );
        const outputUiId =
            nestedRoute?.produces_chemical_ui_id ||
            nestedTargetStage?.produces_chemical_ui_id ||
            stage.produces_chemical_ui_id;
        if (outputUiId) {
            stage.produces_chemical_ui_id = outputUiId;
            if (nestedRoute) {
                nestedRoute.produces_chemical_ui_id = outputUiId;
            }
        }
        return { outputUiId };
    }

    const reaction = resolveReactionFromRouteStage(
        route as ILibraryMultiStepReaction,
        stage,
        new Map()
    );
    const productChemical = findReactionProductChemical(
        reaction,
        stage.produces_chemical_ui_id || reaction?.produces_chemical_ui_id
    );
    const outputUiId =
        productChemical?.ui_id ||
        stage.produces_chemical_ui_id ||
        reaction?.produces_chemical_ui_id;
    if (outputUiId) {
        stage.produces_chemical_ui_id = outputUiId;
        if (reaction) {
            reaction.produces_chemical_ui_id = outputUiId;
        }
        if (productChemical) {
            productChemical.ui_id = outputUiId;
        }
    }

    return {
        outputUiId,
        outputChemical: productChemical,
    };
};

const findSuccessorInputChemical = (
    reaction: ILibraryReaction | undefined,
    dependencyViaChemicalUiId: string | undefined,
    predecessorOutput: ResolvedStageOutput
): { chemical?: ReactionChemical; matchKind?: ChemicalIdentityMatchKind } => {
    const inputs = (reaction?.chemicals || []).filter(isRouteInputChemical);
    const exactInput = dependencyViaChemicalUiId
        ? inputs.find(
              (chemical) => chemical.ui_id === dependencyViaChemicalUiId
          )
        : undefined;
    if (exactInput) {
        return {
            chemical: exactInput,
            matchKind: "UI_ID",
        };
    }

    const predecessorIdentity = getChemicalIdentity(
        predecessorOutput.outputChemical,
        dependencyViaChemicalUiId || predecessorOutput.outputUiId
    );
    for (const input of inputs) {
        const matchKind = getChemicalIdentityMatchKind(
            predecessorIdentity,
            getChemicalIdentity(input)
        );
        if (matchKind) {
            return {
                chemical: input,
                matchKind,
            };
        }
    }

    return {};
};

const addRouteChemicalIdentityRepair = (
    context: RouteChemicalIdentityContext,
    repair: RouteChemicalIdentityRepair
) => {
    context.repairs.push(repair);
    if (repair.match_kind && repair.match_kind !== "UI_ID") {
        context.warnings.push(repair.message);
    }
};

const normalizeReactionRouteChemicalIdentityInPlace = (
    route: ReactionRoute,
    context: RouteChemicalIdentityContext,
    visited = new Set<string>()
): void => {
    if (route.ui_id) {
        if (visited.has(route.ui_id)) {
            return;
        }
        visited.add(route.ui_id);
    }

    (route.stages || []).forEach((stage) => {
        if (stage.stage_kind === "ROUTE" && stage.nested_route) {
            normalizeReactionRouteChemicalIdentityInPlace(
                stage.nested_route,
                context,
                visited
            );
            const nestedTargetStage = resolveRouteTargetStage(
                stage.nested_route as ILibraryMultiStepReaction
            );
            const nestedOutputUiId =
                stage.nested_route.produces_chemical_ui_id ||
                nestedTargetStage?.produces_chemical_ui_id;
            if (nestedOutputUiId) {
                stage.produces_chemical_ui_id = nestedOutputUiId;
            }
        }
    });

    const stagesById = new Map(
        (route.stages || [])
            .filter((stage) => !!stage.ui_id)
            .map((stage) => [stage.ui_id as string, stage])
    );

    (route.dependencies || []).forEach((dependency) => {
        const predecessorStage = dependency.predecessor_stage_ui_id
            ? stagesById.get(dependency.predecessor_stage_ui_id)
            : undefined;
        const successorStage = dependency.successor_stage_ui_id
            ? stagesById.get(dependency.successor_stage_ui_id)
            : undefined;
        if (!predecessorStage || !successorStage) {
            return;
        }

        const originalDependencyViaChemicalUiId = dependency.via_chemical_ui_id;
        const predecessorOutput = resolveStageOutputForChemicalIdentity(
            route,
            predecessorStage
        );
        const canonicalOutputUiId =
            predecessorOutput.outputUiId || originalDependencyViaChemicalUiId;
        if (!canonicalOutputUiId) {
            return;
        }

        if (dependency.via_chemical_ui_id !== canonicalOutputUiId) {
            addRouteChemicalIdentityRepair(context, {
                predecessor_stage_ui_id: predecessorStage.ui_id,
                successor_stage_ui_id: successorStage.ui_id,
                from_ui_id: dependency.via_chemical_ui_id,
                to_ui_id: canonicalOutputUiId,
                match_kind: "UI_ID",
                message:
                    "Route dependency chemical identity was aligned with the producing stage output.",
            });
            dependency.via_chemical_ui_id = canonicalOutputUiId;
        }

        const successorReaction = resolveReactionFromRouteStage(
            route as ILibraryMultiStepReaction,
            successorStage,
            new Map()
        );
        const successorInput = findSuccessorInputChemical(
            successorReaction,
            originalDependencyViaChemicalUiId,
            predecessorOutput
        );
        if (!successorInput.chemical) {
            context.warnings.push(
                "Route dependency target could not be matched to a successor reaction input."
            );
            return;
        }

        const previousInputUiId = successorInput.chemical.ui_id;
        if (previousInputUiId !== canonicalOutputUiId) {
            successorInput.chemical.ui_id = canonicalOutputUiId;
            addRouteChemicalIdentityRepair(context, {
                predecessor_stage_ui_id: predecessorStage.ui_id,
                successor_stage_ui_id: successorStage.ui_id,
                from_ui_id: previousInputUiId,
                to_ui_id: canonicalOutputUiId,
                match_kind: successorInput.matchKind,
                message:
                    successorInput.matchKind === "UI_ID"
                        ? "Successor input chemical identity was aligned with the dependency output."
                        : "Successor input chemical identity was aligned by matching chemical structure.",
            });
        }
    });

    (route.stages || []).forEach((stage) =>
        resolveStageOutputForChemicalIdentity(route, stage)
    );

    const targetStage = resolveRouteTargetStage(
        route as ILibraryMultiStepReaction
    );
    if (targetStage?.produces_chemical_ui_id) {
        route.produces_chemical_ui_id = targetStage.produces_chemical_ui_id;
    }
};

export const normalizeReactionRouteChemicalIdentity = <T extends ReactionRoute>(
    route: T
): NormalizeReactionRouteChemicalIdentityResult<T> => {
    const clonedRoute = cloneRouteForChemicalIdentity(route);
    const context: RouteChemicalIdentityContext = {
        repairs: [],
        warnings: [],
    };
    normalizeReactionRouteChemicalIdentityInPlace(clonedRoute, context);
    return {
        route: clonedRoute,
        repairs: context.repairs,
        warnings: Array.from(new Set(context.warnings)),
    };
};

const setRouteApiReaction = (
    reactions: Record<string, ILibraryReaction>,
    key: string | undefined,
    reaction: ILibraryReaction | undefined
) => {
    if (!key || !reaction) {
        return;
    }
    const existing = reactions[key];
    if (
        !existing ||
        scoreReactionRichness(reaction) > scoreReactionRichness(existing)
    ) {
        reactions[key] = reaction;
    }
};

const normalizeReactionRouteApiReactionsInPlace = (
    route: ReactionRoute,
    reactionLookup: Map<string, ILibraryReaction>,
    visited = new Set<string>()
): void => {
    if (route.ui_id) {
        if (visited.has(route.ui_id)) {
            return;
        }
        visited.add(route.ui_id);
    }

    const apiReactions: Record<string, ILibraryReaction> = {};
    const stages = route.stages || [];

    if (stages.length > 0) {
        stages.forEach((stage) => {
            const stageKind = stage.stage_kind || "REACTION";
            if (stageKind === "ROUTE") {
                if (stage.nested_route) {
                    normalizeReactionRouteApiReactionsInPlace(
                        stage.nested_route,
                        reactionLookup,
                        visited
                    );
                }
                return;
            }

            const resolvedReaction = resolveReactionFromRouteStage(
                route as ILibraryMultiStepReaction,
                stage,
                reactionLookup
            );
            const reactionUiId =
                stage.reaction_ui_id || resolvedReaction?.ui_id || stage.ui_id;
            if (reactionUiId && !stage.reaction_ui_id) {
                stage.reaction_ui_id = reactionUiId;
            }

            setRouteApiReaction(apiReactions, reactionUiId, resolvedReaction);
        });
    } else {
        Object.entries(route.reactions || {}).forEach(([key, reaction]) => {
            const reactionUiId = reaction?.ui_id || key;
            setRouteApiReaction(apiReactions, reactionUiId, reaction);
        });
    }

    if (Object.keys(apiReactions).length > 0) {
        route.reactions = apiReactions;
    }
};

export const normalizeReactionRouteForApi = <T extends ReactionRoute>(
    route: T,
    reactionLookup: Map<string, ILibraryReaction> = new Map()
): T => {
    const targetNormalizedRoute = normalizeRouteTargetStage(
        route as T & ILibraryMultiStepReaction
    );
    const identityNormalizedRoute = normalizeReactionRouteChemicalIdentity(
        targetNormalizedRoute
    ).route;
    const routeWithTargetStage = normalizeRouteTargetStage(
        identityNormalizedRoute as T & ILibraryMultiStepReaction
    );
    normalizeReactionRouteApiReactionsInPlace(
        routeWithTargetStage,
        reactionLookup
    );
    return routeWithTargetStage as T;
};

export const buildRouteReactionMap = (
    route: ILibraryMultiStepReaction,
    reactionLookup: Map<string, ILibraryReaction>,
    routeLookup: Map<string, ILibraryMultiStepReaction> = new Map()
): Record<string, ILibraryReaction> => {
    const sourceByKey = new Map<string, ILibraryReaction>();
    const sourceByReactionUiId = new Map<string, ILibraryReaction>();
    const registerReaction = (key: string, reaction: ILibraryReaction) => {
        if (!reaction) {
            return;
        }
        const existing = sourceByKey.get(key);
        if (
            !existing ||
            scoreReactionRichness(reaction) > scoreReactionRichness(existing)
        ) {
            sourceByKey.set(key, reaction);
        }
        if (reaction.ui_id) {
            const existingByUiId = sourceByReactionUiId.get(reaction.ui_id);
            if (
                !existingByUiId ||
                scoreReactionRichness(reaction) >
                    scoreReactionRichness(existingByUiId)
            ) {
                sourceByReactionUiId.set(reaction.ui_id, reaction);
            }
        }
    };

    Object.entries(route.reactions || {}).forEach(([key, reaction]) =>
        registerReaction(key, reaction)
    );

    const stages = route.stages || [];
    if (stages.length > 0) {
        const sortedStages = [...stages].sort((a, b) => {
            const aOrder = a.display_order ?? Number.MAX_SAFE_INTEGER;
            const bOrder = b.display_order ?? Number.MAX_SAFE_INTEGER;
            return aOrder - bOrder;
        });
        const map: Record<string, ILibraryReaction> = {};

        sortedStages.forEach((stage, index) => {
            const stageId =
                stage.ui_id ||
                stage.reaction_ui_id ||
                stage.nested_route_ui_id ||
                `stage-${index + 1}`;
            const stageKind = stage.stage_kind || "REACTION";
            const stepByIndex = sourceByKey.get(`step${index + 1}`);
            const byStageId = sourceByKey.get(stageId);
            const byReactionKey = stage.reaction_ui_id
                ? sourceByKey.get(stage.reaction_ui_id)
                : undefined;
            const byReactionUiId = stage.reaction_ui_id
                ? sourceByReactionUiId.get(stage.reaction_ui_id)
                : undefined;
            const fromReactionLookup = stage.reaction_ui_id
                ? reactionLookup.get(stage.reaction_ui_id)
                : undefined;
            const resolvedFromStage =
                stageKind === "REACTION"
                    ? resolveReactionFromRouteStage(
                          {
                              ...route,
                              reactions: Object.fromEntries(
                                  Array.from(sourceByKey.entries())
                              ),
                          },
                          stage,
                          reactionLookup
                      )
                    : undefined;
            const resolvedFromNestedRoute =
                stageKind === "ROUTE"
                    ? resolveFinalReactionForRouteStage(
                          stage,
                          routeLookup,
                          reactionLookup
                      )
                    : undefined;
            const candidates = [
                byStageId,
                byReactionKey,
                byReactionUiId,
                fromReactionLookup,
                resolvedFromStage,
                stageKind === "REACTION" ? stepByIndex : undefined,
                resolvedFromNestedRoute,
            ].filter(Boolean) as ILibraryReaction[];
            const resolvedReactionStage = candidates.reduce<
                ILibraryReaction | undefined
            >((best, candidate) => {
                if (!best) {
                    return candidate;
                }
                return scoreReactionRichness(candidate) >
                    scoreReactionRichness(best)
                    ? candidate
                    : best;
            }, undefined);

            if (resolvedReactionStage) {
                map[stageId] = resolvedReactionStage;
                return;
            }

            map[stageId] = {
                ui_id:
                    stageKind === "ROUTE"
                        ? stage.nested_route_ui_id || stageId
                        : stage.reaction_ui_id || stageId,
                name:
                    stage.name ||
                    (stageKind === "ROUTE"
                        ? stage.nested_route_ui_id
                        : stage.reaction_ui_id) ||
                    stageId,
                chemicals: [],
            };
        });

        return map;
    }

    const fallbackEntries = Array.from(sourceByKey.entries());
    if (fallbackEntries.length > 0) {
        const fallbackMap: Record<string, ILibraryReaction> = {};
        fallbackEntries.forEach(([key, reaction], index) => {
            const fallbackId = reaction.ui_id || key || `stage-${index + 1}`;
            fallbackMap[fallbackId] = reaction;
        });
        return fallbackMap;
    }

    return {};
};

export const normalizeLibraryMultiStepReaction = (
    route: ILibraryMultiStepReaction,
    reactionLookup: Map<string, ILibraryReaction> = new Map(),
    routeLookup: Map<string, ILibraryMultiStepReaction> = new Map()
): ILibraryMultiStepReaction => {
    const identityNormalizedRoute =
        normalizeReactionRouteChemicalIdentity(route).route;
    const routeWithTargetStage = normalizeRouteTargetStage(
        identityNormalizedRoute
    );
    const normalizedReactions = buildRouteReactionMap(
        routeWithTargetStage,
        reactionLookup,
        routeLookup
    );
    return {
        ...routeWithTargetStage,
        reactions: normalizedReactions,
    };
};

export const resolveRouteForAnalyzer = (
    route: ILibraryMultiStepReaction,
    reactionLookup: Map<string, ILibraryReaction>,
    routeLookup: Map<string, ILibraryMultiStepReaction> = new Map()
): IMultiStepReaction => {
    const normalizedRoute = normalizeLibraryMultiStepReaction(
        route,
        reactionLookup,
        routeLookup
    );
    return {
        ...normalizedRoute,
        ui_id:
            normalizedRoute.ui_id || normalizedRoute.name || "reaction-route",
        reactions: normalizedRoute.reactions || {},
    };
};
